import {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {API_ERROR_MESSAGES} from 'utils/api';

/**
 * A hook that provides a cancellable dispatch function and an abort function for
 * cancelling the currently dispatched thunk.  Both a callback & a abortSignal are passed to the action creator, allow you to listen for an abort event for the thunk and cancel axios the request as well.
 * @see https://redux-toolkit.js.org/api/createAsyncThunk#canceling-while-running
 *
 * @example
 *
 * // ComponentFile.jsx
 * const {cancellableDispatch, abortCurrent} = useCancellableDispatch();
 * useEffect(() => {
 *     cancellableDispatch(fetchFacilities, {cityId: 1});
 *     return () => {
 *         abortCurrent();
 *     };
 * }, [cancellableDispatch, abortCurrent]);
 *
 * // actionFile.js
 * const fetchFacilities = createAsyncThunk('fetchFacilities', async ({cityId, handleAbortSignal, abortSignal}, {signal}) => {
 *   handleAbortSignal(signal);
 *   const response = await getFacilitiesApi(facilityId, {signal: abortSignal}});
 * });
 *
 * @returns {{
 *   cancellableDispatch: function,
 *   abortCurrent: function
 * }}
 */
const useCancellableDispatch = () => {
    const dispatch = useDispatch();
    const currentRequestRef = useRef({
        abortSignal: null,
        actionPromise: null,
    });

    /**
     * Dispatches an action with the ability to cancel any ongoing operation.
     *
     * @param {function} actionCreator - The action creator function to dispatch.  Generally a thunk.
     * @param {Array} args - The arguments to pass to the action creator.
     * @returns {Promise} - A promise that resolves when the action is completed or rejects if the action is canceled.
     */
    const cancellableDispatch = useCallback(
        async (actionCreator, args) => {
            // Abort any ongoing operation
            if (currentRequestRef.current.actionPromise) {
                currentRequestRef.current.actionPromise.abort();
            }

            /*
            Create a new abortSignal for each operation.
            @see https://axios-http.com/docs/cancellation
            */
            const controller = new AbortController();

            const handleAbortSignal = (signal) => {
                signal.addEventListener('abort', () => {
                    controller.abort();
                });
            };

            // Dispatch the action with the abortSignal & signal handler as part of the arguments
            const actionPromise = dispatch(
                actionCreator({
                    ...args,
                    abortSignal: controller.signal,
                    handleAbortSignal,
                })
            );

            // Update the current request reference
            currentRequestRef.current = {
                abortSignal: controller.signal,
                actionPromise,
            };

            try {
                // Await the action promise
                const result = await actionPromise.unwrap();

                return result;
            } catch (error) {
                if (error.message === API_ERROR_MESSAGES.ABORTED) {
                    // If the error is an abort error, return the error but don't throw
                    return error;
                }

                // Otherwise, throw the every other error
                throw error;
            }
        },
        [dispatch]
    );

    /**
     * Callback function to abort the current action promise.
     * If the current request has an action promise, it will be aborted and the current request will be reset.
     *
     * @returns {void}
     *
     * @example
     * // Call the abortCurrent function
     * abortCurrent();
     */
    const abortCurrent = useCallback(() => {
        if (currentRequestRef.current.actionPromise) {
            currentRequestRef.current.actionPromise.abort();
            currentRequestRef.current = {
                abortSignal: null,
                actionPromise: null,
            };
        }
    }, []);

    return {cancellableDispatch, abortCurrent};
};

export default useCancellableDispatch;
